import { IAlbum } from 'ngx-lightbox';
import ProductAsset from '../../../model/product-asset';
import AssetHelper from '../../../helpers/asset-helper';
import Address from 'src/app/model/project/address';
import {
  SubProject,
  SUB_PROJECT_TYPES,
  TypeToDisplayAndClass
} from 'src/app/model/project/sub-project';
import ProjectHelper from 'src/app/helpers/project-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import { StringHelper } from 'src/app/helpers/string-helper';
import Board from 'src/app/model/product/board/board';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import Employee from 'src/app/model/product/employee';
import { clone } from 'lodash';
import IPBoard from 'src/app/model/product/ip-board/ip-board';

export default class MostRecentPlacementHelper {
  private constructor() {}

  public static addSubProjectDisplays(
    subProject: SubProject,
    accumulator: SubProjectDisplay[]
  ): void {
    const type = TypeToDisplayAndClass.get(subProject.type);
    const hoursAndMinutes = GeneralHelper.getMinutesInHourAndMinutesArray(
      GeneralHelper.getTotalExecutionMinutes(
        subProject.projectPlacement.registeredActions
      )
    );
    const subProjectAssets: SubProjectAssetObject = this.getSubProjectAssets(
      subProject
    );
    const toAdd: SubProjectDisplay = {
      id: subProject.id,
      projectId: subProject.project.id,
      type: subProject.type,
      typeClass: type.class,
      typeDisplay: type.display,
      customerDisplay: this.getCustomerDisplay(subProject),
      executionDate: subProject.currentExecutionDate,
      placementTimeHours: '' + hoursAndMinutes[0],
      placementTimeMinutes:
        hoursAndMinutes[1] < 10
          ? `0${hoursAndMinutes[1]}`
          : '' + hoursAndMinutes[1],
      address: subProject.projectPlacement.address,
      firstPlacer: ProjectHelper.getFirstPlacer(subProject),
      firstAsset: subProjectAssets == null ? null : subProjectAssets.firstAsset,
      lightboxAlbum:
        subProjectAssets == null ? null : subProjectAssets.lightboxAlbum,
      products: null
    };
    if (subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      this.handleBaseProjectProducts(subProject, toAdd, accumulator);
    } else {
      this.handleAdjustmentProducts(subProject, toAdd, accumulator);
    }
  }

  private static getCustomerDisplay(subProject: SubProject): string {
    return subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.projectContacts) ||
      subProject.project.projectContacts[0].company == null
      ? null
      : subProject.project.projectContacts[0].company.regularName;
  }

  private static handleBaseProjectProducts(
    subProject: SubProject,
    display: SubProjectDisplay,
    accumulator: SubProjectDisplay[]
  ): void {
    if (CollectionHelper.isPopulated(subProject.project.products)) {
      subProject.project.products.forEach((p, i) => {
        display = i > 0 ? clone(display) : display;
        display.products = [this.getProductDisplay(p, true)];
        accumulator.push(display);
      });
    }
  }

  private static handleAdjustmentProducts(
    subProject: SubProject,
    display: SubProjectDisplay,
    accumulator: SubProjectDisplay[]
  ): void {
    display.products = CollectionHelper.isPopulated(subProject.project.products)
      ? subProject.project.products.map(p => this.getProductDisplay(p, false))
      : [];
    accumulator.push(display);
  }

  private static getProductDisplay(
    product: Product,
    isBaseProject: boolean
  ): ProductDisplay {
    return {
      typeDisplay: this.getProductTypeDisplay(product),
      formatDisplay: ProductHelper.getProductSizeDisplay(product),
      firstAsset:
        isBaseProject && CollectionHelper.isPopulated(product.productAssets)
          ? product.productAssets[0]
          : null,
      lightboxAlbum:
        isBaseProject && CollectionHelper.isPopulated(product.productAssets)
          ? product.productAssets.map(pa => AssetHelper.toIAlbum(pa))
          : null
    };
  }

  private static getProductTypeDisplay(product: Product): string {
    switch (product.type) {
      case ProductTypes.WINDOWSTICKER.value:
      case ProductTypes.CUSTOMWINDOWSTICKER.value:
        return 'Raamsticker';
      case ProductTypes.BOARD.value:
        const board = <Board>product;
        const firstBoardPart = CollectionHelper.isPopulated(board.boardParts)
          ? board.boardParts[0]
          : null;
        return firstBoardPart == null
          ? null
          : StringHelper.join(
              [
                firstBoardPart.boardType == null
                  ? null
                  : firstBoardPart.boardType.label,
                ProductHelper.calculatePlacementTypeDisplay(
                  firstBoardPart.placementType == null
                    ? null
                    : firstBoardPart.placementType.label,
                  firstBoardPart.otherPlacementType
                )
              ],
              ' '
            );
      case ProductTypes.OTHER_PRODUCT.value:
        const otherProduct = <OtherProduct>product;
        return StringHelper.isEmpty(otherProduct.otherProductName)
          ? 'Ander product'
          : otherProduct.otherProductName;
      case ProductTypes.IP_BOARD.value:
        const ipBoard = <IPBoard>product;
        return ipBoard.boardPart == null
          ? null
          : StringHelper.join(
              [
                ipBoard.boardPart.boardType == null
                  ? null
                  : ipBoard.boardPart.boardType.label,
                ProductHelper.calculatePlacementTypeDisplay(
                  ipBoard.boardPart.placementType == null
                    ? null
                    : ipBoard.boardPart.placementType.label,
                  ipBoard.boardPart.alternativePlacementType
                )
              ],
              ''
            );
      default:
        throw new Error(`Unrecognized product type: ${product.type}`);
    }
  }

  private static getSubProjectAssets(
    subProject: SubProject
  ): SubProjectAssetObject {
    return subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT ||
      subProject.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        subProject.projectPlacement.projectPlacementAssets
      )
      ? null
      : {
          firstAsset: subProject.projectPlacement.projectPlacementAssets[0],
          lightboxAlbum: subProject.projectPlacement.projectPlacementAssets.map(
            pa => AssetHelper.toIAlbum(pa)
          )
        };
  }
}

interface SubProjectAssetObject {
  firstAsset?: ProductAsset;
  lightboxAlbum?: IAlbum[];
}

export interface SubProjectDisplay {
  id: number;
  projectId: number;
  type: string;
  typeClass: string;
  typeDisplay: string;
  customerDisplay: string;
  executionDate: Date;
  placementTimeHours: string;
  placementTimeMinutes: string;
  address: Address;
  firstPlacer: Employee;
  firstAsset?: ProductAsset;
  lightboxAlbum?: IAlbum[];
  products: ProductDisplay[];
}

export interface ProductDisplay {
  typeDisplay: string;
  formatDisplay: string;
  firstAsset?: ProductAsset;
  lightboxAlbum?: IAlbum[];
}
